// https://stackoverflow.com/a/16861050

import { Observable } from 'rxjs'

export function openWindow(url, callback?, w = 500, h = 600) {
  // Fixes dual-screen position                         Most browsers      Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
    ? document.documentElement.clientWidth
    : screen.width
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
    ? document.documentElement.clientHeight
    : screen.height

  const left = width / 2 - w / 2 + dualScreenLeft
  const top = height / 2 - h / 2 + dualScreenTop

  const messageHandler = (event) => {
    if (event.origin.indexOf('.tokeet.com') > -1 || event.origin.indexOf('.advance.cm') > -1) {
      if (callback && callback(event)) {
        window.removeEventListener('message', messageHandler)
      }
    }
  }
  if (callback) {
    window.addEventListener('message', messageHandler)
  }
  const parsedUrl = new URL(url)
  // Opens a new window
  const newWindow = window.open(
    url,
    parsedUrl.hostname,
    `location=yes,width=${w}, height=${h}, modal=yes, alwaysRaised=yes, top=${top}, left=${left}`
  )
  // Puts focus on the newWindow
  if (window.focus && newWindow) {
    newWindow.focus()
  }
  return newWindow
}

export interface OAuthPopupResult {
  status: 'success' | 'error' | 'closed' | 'timeout' | 'blocked'
  reason?: string
  data?: any
}

const OAUTH_POPUP_TIMEOUT = 5 * 60 * 1000 // Airbnb auth codes expire after 10 minutes; give up well before that.

/**
 * Opens an OAuth popup and reports how it ended, exactly once:
 * - `success` — the popup posted `'success'` or `{ status: 'success' }` from a trusted origin
 * - `error`   — the popup posted `'failure'` or `{ status: 'error', reason }`
 * - `closed`  — the user closed the popup before any result arrived
 * - `timeout` — no result arrived within `timeoutMs`
 * - `blocked` — the browser refused to open the popup
 */
export function openOAuthWindow(url: string, w = 500, h = 600, timeoutMs = OAUTH_POPUP_TIMEOUT) {
  return new Observable<OAuthPopupResult>((observer) => {
    let settled = false
    let closedTimer
    let timeoutTimer

    const cleanup = () => {
      window.removeEventListener('message', messageHandler)
      clearInterval(closedTimer)
      clearTimeout(timeoutTimer)
    }

    const settle = (result: OAuthPopupResult) => {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      observer.next(result)
      observer.complete()
    }

    const messageHandler = (event: MessageEvent) => {
      if (event.origin.indexOf('.tokeet.com') < 0 && event.origin.indexOf('.advance.cm') < 0) {
        return
      }
      const data = event.data
      if (data === 'success') {
        settle({ status: 'success' })
      } else if (data === 'failure') {
        settle({ status: 'error', reason: 'connect_failed' })
      } else if (data && typeof data === 'object' && data.status) {
        if (data.status === 'success') {
          settle({ status: 'success', data })
        } else {
          settle({ status: 'error', reason: data.reason || 'connect_failed', data })
        }
      }
      // any other message on this window is unrelated — keep waiting
    }

    window.addEventListener('message', messageHandler)

    const popup = openWindow(url, undefined, w, h)
    if (!popup) {
      settle({ status: 'blocked' })
      return cleanup
    }

    closedTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(closedTimer)
        // a result page may have posted its message right before closing — let it win
        setTimeout(() => settle({ status: 'closed' }), 500)
      }
    }, 500)

    timeoutTimer = setTimeout(() => {
      try {
        popup.close()
      } catch (e) {}
      settle({ status: 'timeout' })
    }, timeoutMs)

    return cleanup
  })
}
